import Swal from 'sweetalert2';
import { ReportsCommand } from '../../../services/command';
import { RefundRestQuery } from '../../../services/query';
import { RefundReasonEnum } from '../../../types/enums/refund-reason-enum';
import { RefundStatusEnum } from '../../../types/enums/refund-status-enum';
import Helper from '../../../utils/helper';

export type RefundTab = { id: number; name: string };

export const groupPaymentsByOrderNumber = (payments: any[]) =>
    payments.reduce((acc: Record<string, any>, payment: any) => {
        acc[payment.orderNumber] = acc[payment.orderNumber] || payment;
        return acc;
    }, {});

export const getUniquePayments = (items: any[] | undefined) => Object.values(groupPaymentsByOrderNumber(items ?? []));

export const hasOpenRefundDetailStatus = (refundDetails: any[] | undefined) =>
    refundDetails?.some((detail: any) => detail.refundDetailStatusId === 1 || detail.refundDetailStatusId === 3) ??
    false;

export const ensureFinishedRefundsTab = (tabList: RefundTab[], finishedRefundsLabel: string): RefundTab[] => {
    if (tabList.some((tab) => tab.id === 2)) {
        return tabList;
    }

    const updatedTabList = [...tabList];
    updatedTabList.splice(1, 0, { id: 2, name: finishedRefundsLabel });
    return updatedTabList;
};

export type TabListValidationLabels = {
    finishedRefunds: string;
    waitingRefunds: string;
    paymentConversion: string;
};

export const buildTabListForValidation = (
    prevTabList: RefundTab[],
    isValidFromApi: boolean | null,
    hasRefundDetailStatusOne: boolean,
    labels: TabListValidationLabels
): { tabList: RefundTab[]; activeTabIndex?: number } => {
    let updatedTabList = ensureFinishedRefundsTab(prevTabList, labels.finishedRefunds);
    const tab5Exists = updatedTabList.some((tab) => tab.id === 5);
    const tab6Exists = updatedTabList.some((tab) => tab.id === 6);

    if (isValidFromApi === true) {
        updatedTabList = updatedTabList.filter((tab) => tab.id !== 5);
        if (!tab6Exists) {
            updatedTabList = [...updatedTabList, { id: 6, name: labels.paymentConversion }];
            return { tabList: updatedTabList, activeTabIndex: 6 };
        }
        return { tabList: updatedTabList };
    }

    updatedTabList = updatedTabList.filter((tab) => tab.id !== 6);
    if (hasRefundDetailStatusOne && !tab5Exists) {
        updatedTabList = [...updatedTabList, { id: 5, name: labels.waitingRefunds }];
        return { tabList: updatedTabList, activeTabIndex: 5 };
    }

    return { tabList: updatedTabList };
};

export const canValidateRefundMethod = (paymentListInfo: any, refundEntity: any) =>
    Boolean(
        paymentListInfo?.items?.length > 0 &&
        refundEntity.refundDetails?.[0]?.refundMethodId &&
        paymentListInfo.items[0]?.paymentMethodCollectorId
    );

export const resolveValidationIsValid = (response: any): boolean | null => {
    if (response && Object.prototype.hasOwnProperty.call(response, 'isValid')) {
        return response.isValid;
    }
    return null;
};

export const addPrepareRefundTransferTab = (tabList: RefundTab[], prepareRefundTransferLabel: string): RefundTab[] => {
    if (tabList.some((tab) => tab.id === 4)) {
        return tabList;
    }
    return [...tabList, { id: 4, name: prepareRefundTransferLabel }];
};

export const shouldShowPrepareRefundTab = (returnData: any, isMpApprover: boolean) =>
    Boolean(returnData?.isMarketPlaceReturn && (Helper.isStoreEmployeeForFrontend() || isMpApprover));

export const getCurrencySymbol = (currencyId: number | undefined) => {
    const selectedCountry = JSON.parse(localStorage.getItem('selectedCountry') || '{}');
    return selectedCountry?.supportedCurrencies?.find((currency: any) => currency?.currencyId === currencyId)
        ?.shortCode;
};

export const shouldShowReturnInvoiceLink = (getRefundDetails: any) =>
    Boolean(
        getRefundDetails?.isReturnInvoiceShowed &&
        getRefundDetails?.entity?.refundStatusId === RefundStatusEnum.Completed &&
        (getRefundDetails?.entity?.refundReasonId === RefundReasonEnum.ReturnRefund ||
            getRefundDetails?.entity?.refundReasonId === RefundReasonEnum.LostPackageRefund ||
            getRefundDetails?.entity?.refundReasonId === RefundReasonEnum.ShipmentFeesRefund)
    );

export const downloadPdfFromBlob = (blob: Blob, fileName: string) => {
    const pdfUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(pdfUrl);
};

export const downloadExpenseBill = async (
    getRefundDetails: any,
    t: (key: string) => string,
    onError: (message: string) => void
) => {
    const params = {
        orderId: getRefundDetails?.entity.orderId,
        shipmentId: getRefundDetails?.entity.shippingListId,
        refundId: getRefundDetails?.entity.refundId,
        customerId: getRefundDetails?.entity.customerId,
    };

    try {
        const response = await ReportsCommand.GetExpenseBill(params);
        if (!response) {
            return;
        }

        try {
            downloadPdfFromBlob(response, `EX${getRefundDetails?.entity.refundId}.pdf`);
        } catch (error) {
            console.error('Error downloading PDF:', error);
            onError(t('orderDetail.eWaybillNotFound'));
        }
    } catch (error) {
        console.error('PDF indirme hatası:', error);
        onError(t('refund.downloadError'));
    }
};

export const fetchRefundMethodValidation = async (refundEntity: any, paymentListInfo: any): Promise<boolean | null> => {
    const refundMethodId = refundEntity.refundDetails[0].refundMethodId;
    const paymentMethodCollectorId = paymentListInfo.items[0].paymentMethodCollectorId;

    try {
        const response = await RefundRestQuery.GetRefundMethodValidation({
            RefundMethodId: refundMethodId,
            PaymentMethodCollectorId: paymentMethodCollectorId,
            RefundStatusId: refundEntity.refundStatusId,
        });
        return resolveValidationIsValid(response);
    } catch (error) {
        console.error('Error fetching refund method validation:', error);
        return null;
    }
};

export const showSwalError = (title: string, message = '') => {
    Swal.fire(title, message, 'error');
};

export const openExternalLink = (url: string, target?: string) => {
    window.open(url, target);
};
